import { useJsApiLoader } from '@react-google-maps/api';

const GOOGLE_MAPS_SCRIPT_ID = 'google-map-script';
const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

// Captured once at module load. The env var doesn't change at runtime,
// so the HAS_KEY branch in useMapsApiLoader is stable for the lifetime
// of the bundle — every render of every consuming component takes the
// same path. This is what makes the conditional hook call safe.
const API_KEY = import.meta.env.VITE_GOOGLE_MAP_KEY ?? '';
const HAS_KEY = !!API_KEY;

/**
 * Single entry point for loading the Google Maps JS API.
 *
 * Why this exists rather than calling `useJsApiLoader` directly:
 *
 * - In local dev the key is intentionally unset. Calling
 *   `useJsApiLoader` with an empty key still injects the
 *   `maps.googleapis.com/maps/api/js?key=` script, and Google's
 *   runtime then injects a "this page can't load Google Maps
 *   correctly" `dialog` onto `document.body` — outside React's tree,
 *   so it survives form unmount and leaves a residual border on the
 *   page. We sidestep the whole thing by skipping the loader when no
 *   key is configured; callers' existing `isLoaded ? <Autocomplete>
 *   : <Input>` branches render the plain input.
 *
 * - Same id + libraries shared across all three Maps consumers
 *   (NewPropertyForm, usePropertyForm, GoogleMap) — co-locate the
 *   config so a future tweak can't drift between them.
 */
export function useMapsApiLoader(): { isLoaded: boolean } {
  if (!HAS_KEY) return { isLoaded: false };
  // HAS_KEY is a module-level constant — the branch above is stable
  // across all renders of all consumers, so this isn't a true
  // conditional hook call. Rules-of-hooks lint can't prove that
  // statically, so we suppress with this note.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
}
